import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useAMedias } from '../context/AMediasContext';
import { useTareas } from '../context/TareasContext';
import { usePerfil } from '../context/PerfilContext';
import { usePlus } from '../context/PlusContext';
import { ClaveLimite, LIMITES_GRATIS } from '../config/planes';
import { cuentasNormales, huchas, inversionesActivas, planesOrganizados } from '../utils/limites';
import { casasCompartidasOrganizadas, tareasQueSeRepiten } from '../utils/tareas';

/**
 * Un límite del plan gratis visto desde un botón de «Nuevo…»:
 *
 *   const lim = useLimite('cuentas');
 *   <button onClick={() => lim.intentar(() => abrirModal())}>
 *   {lim.texto}   // «2 de 3 cuentas», o '' con Plus
 *
 * `intentar` abre la hoja de Plus en vez de la acción si ya no queda hueco.
 */
export function useLimite(clave: ClaveLimite, casaId?: string) {
  const { user } = useAuth();
  const { esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();
  const d = useData();
  const { planes } = useAMedias();
  const t = useTareas();

  const usados = {
    cuentas: () => cuentasNormales(d.cuentas),
    huchas: () => huchas(d.cuentas),
    categorias: () => d.categorias.length,
    presupuestos: () => d.presupuestos.length,
    periodicos: () => d.traspasosRecurrentes.length + d.transaccionesRecurrentes.length,
    inversiones: () => inversionesActivas(d.inversiones),
    planesAMedias: () => planesOrganizados(planes, user?.uid),
    casasCompartidas: () => casasCompartidasOrganizadas(t.casas, user?.uid),
    // Por casa: hay que decir cuál
    tareasPeriodicas: () => (casaId ? tareasQueSeRepiten(t.tareas, casaId) : 0),
  }[clave]();

  // En una casa vale también el Plus de quien la organiza
  const conPlus = clave === 'tareasPeriodicas' && casaId ? t.casaConPlus(casaId) : esPlus;
  const maximo = conPlus ? Infinity : LIMITES_GRATIS[clave];
  const quedan = Math.max(0, maximo - usados);

  return {
    usados,
    maximo,
    quedan,
    alcanzado: quedan <= 0,
    /** «2 de 3»; vacío con Plus, para no recordar límites a quien no los tiene. */
    // Con tope 0 (sección de Plus) no hay contador: lo dice el aviso de la página
    texto: conPlus || maximo === 0 ? '' : `${Math.min(usados, maximo)} de ${maximo}`,
    intentar: (accion: () => void, nuevos = 1) => {
      if (usados + nuevos > maximo) ofrecerPlus(clave);
      else accion();
    },
  };
}
